<script setup lang="ts">
import { useStore } from '@/stores'
import { useCartStore } from '@/stores/cart'
import Cart from '@/components/checkout/Cart.vue'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { Dropdown } from 'floating-vue'
import ScrollPanel from 'primevue/scrollpanel'

const store = useStore()
const cartStore = useCartStore()
const autoHide = ref(true)
const dropdownRef = ref<InstanceType<typeof Dropdown> | null>(null)

const emptyCart = computed(() => {
  return store.districts.every(district => cartStore.getCartLines(district.districtId).length === 0)
})

const cartHeight = computed(() => {
  return emptyCart.value ? '50px' : '700px'
})

const handleSetCartAutoHide = (value: boolean) => {
  autoHide.value = value
}

// Close the cart popover after a successful checkout so the user doesn't see auto-re-added assigned fees pop back into an open cart.
const handleCloseDropdown = () => {
  autoHide.value = true
  dropdownRef.value?.hide?.()
}

// Let other surfaces (e.g. the enrollment payment-pending bar) open the cart.
const openCartPopover = () => {
  autoHide.value = true
  dropdownRef.value?.show?.()
}
onMounted(() => window.addEventListener('open-cart', openCartPopover))
onUnmounted(() => window.removeEventListener('open-cart', openCartPopover))
</script>

<template>
  <VDropdown ref="dropdownRef" :distance="6" :auto-hide="autoHide">
    <!-- This is the popover reference (for the events and position) -->
    <slot></slot>
    <!-- This is the content of the popover -->
    <template #popper>
      <div class="px-3 pt-3">
        <div class="text-xl font-bold mb-5">
          {{ $t('c.checkout.checkout.grandTotal') }}
        </div>
        <ScrollPanel :style="{ height: cartHeight }">
          <span v-for="district in store.districts">
            <Cart
              v-if="cartStore.getCartLines(district.districtId).length > 0"
              :district="district"
              :funding-sources="store.fundingSources[district.districtId]"
              :cart-items="cartStore.getCartLines(district.districtId)"
              class="modal-dialog"
              @set-cart-auto-hide="handleSetCartAutoHide"
              @close-dropdown="handleCloseDropdown"
            />
          </span>
          <div v-if="emptyCart" class="text-center w-full">{{ $t('c.cart.empty') }}</div>
        </ScrollPanel>
      </div>
    </template>
  </VDropdown>
</template>

<style scoped></style>
